import React, { useMemo, useState } from 'react';
import { CaretDown, CaretLeft } from '@phosphor-icons/react';
import { useData } from '../../../hooks/useData';
import { useApp } from '../../../contexts/AppContext';
import { TABLES, MODAL_TYPES } from '../../../constants';
import { formatCurrency } from '../../../utils/formatters';
import { formatSelectedOption } from '../../../utils/boqHelpers';
import tableStyles from '../../../styles/tables.module.css';
import pageStyles from '../../../styles/pages.module.css';
import styles from './BudgetPage.module.css';

// project_spaces has no project_id column — it links via floor_id → project_floors.project_id.
// We fetch floors first, then spaces filtered by those floor IDs (same pattern as SpacesPage).

/**
 * NeedsQuotesTab - Shows project items that don't have a selected quote
 *
 * Data source: project_items where active_quote_line_id IS NULL
 * Grouping:
 *   1. by trade type (master_items.vendor_type_id → reference_vendor_types)
 *   2. within each trade, rows with the same master_item_id are merged into
 *      a single row (quantity + cost summed). When the merged row wraps more
 *      than one underlying project_item, it becomes expandable — the dropdown
 *      lists each underlying row with its space name + selected_option.
 * Items without vendor_type_id go under "לא מסווג".
 * Excludes bundle parent wrappers (zero-cost containers).
 *
 * Trade group headers are clickable — collapsed by default, click to expand.
 * Each header also has a "הצעות מחיר" button that opens TradeQuotesModal
 * to manage competing quotes for that trade.
 */
const NeedsQuotesTab = ({ currentProject }) => {
  const projectId = currentProject?.id;
  const { openModal } = useApp();
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [expandedTrades, setExpandedTrades] = useState(() => new Set());

  // Fetch project items (includes relationships, selected_option, space_id)
  const { data: projectItems, isLoading: itemsLoading } = useData(TABLES.PROJECT_ITEMS, {
    select:
      'id, master_item_id, space_id, quantity, total_cost, cost_override, active_quote_line_id, relationships, selected_option',
    filter: { project_id: projectId },
    enabled: !!projectId,
  });

  // Fetch master items for names and vendor_type_id
  const { data: masterItems, isLoading: masterLoading } = useData(TABLES.MASTER_ITEMS, {
    select: 'id, item_name, vendor_type_id, estimated_cost',
    enabled: !!projectId,
  });

  // Fetch vendor types for display names and categories
  const { data: vendorTypes, isLoading: typesLoading } = useData(TABLES.REFERENCE_VENDOR_TYPES, {
    enabled: !!projectId,
  });

  // Fetch project floors first — needed to scope project_spaces by floor_id
  const { data: projectFloors, isLoading: floorsLoading } = useData(TABLES.PROJECT_FLOORS, {
    select: 'id',
    filter: { project_id: projectId },
    enabled: !!projectId,
  });

  const floorIds = useMemo(() => (projectFloors || []).map(f => f.id), [projectFloors]);

  // Fetch project spaces for custom_space_name lookup in detail rows
  const { data: projectSpaces, isLoading: spacesLoading } = useData(TABLES.PROJECT_SPACES, {
    select: 'id, custom_space_name',
    filter: { floor_id: floorIds },
    enabled: !!projectId && floorIds.length > 0,
  });

  const isLoading = itemsLoading || masterLoading || typesLoading || floorsLoading || spacesLoading;

  // Lookup maps
  const masterItemsMap = useMemo(() => {
    if (!masterItems) return new Map();
    return new Map(masterItems.map(mi => [mi.id, mi]));
  }, [masterItems]);

  const vendorTypesMap = useMemo(() => {
    if (!vendorTypes) return new Map();
    return new Map(vendorTypes.map(vt => [vt.id, vt]));
  }, [vendorTypes]);

  const spacesMap = useMemo(() => {
    if (!projectSpaces) return new Map();
    return new Map(projectSpaces.map(s => [s.id, s]));
  }, [projectSpaces]);

  // Build list of unquoted items grouped by trade, then merged by master_item_id
  const groupedItems = useMemo(() => {
    const groups = {};
    const noTradeKey = '__unclassified__';

    (projectItems || []).forEach(pi => {
      if (pi.active_quote_line_id) return; // has a selected quote, skip
      if (pi.relationships?.bundle?.role === 'parent') return; // skip bundle wrappers

      const masterItem = masterItemsMap.get(pi.master_item_id);
      const vendorType = masterItem?.vendor_type_id
        ? vendorTypesMap.get(masterItem.vendor_type_id)
        : null;
      const tradeKey = vendorType?.id || noTradeKey;
      const tradeName = vendorType?.name || 'לא מסווג';
      const category = vendorType?.category || 'zzz';

      if (!groups[tradeKey]) {
        groups[tradeKey] = {
          tradeKey,
          tradeName,
          tradeVendorTypeId: vendorType?.id || null,
          category,
          itemsMap: new Map(),
          projectItemIds: [],
          subtotal: 0,
        };
      }

      // Pricing waterfall: total_cost → cost_override → master estimated_cost
      const estimatedCost = pi.total_cost || pi.cost_override || masterItem?.estimated_cost || 0;
      const qty = pi.quantity || 1;

      // Merge key: master_item_id (fallback to pi.id for items without one)
      const mergeKey = pi.master_item_id || `__orphan_${pi.id}`;

      if (!groups[tradeKey].itemsMap.has(mergeKey)) {
        groups[tradeKey].itemsMap.set(mergeKey, {
          key: mergeKey,
          name: masterItem?.item_name || 'פריט',
          quantity: 0,
          estimatedCost: 0,
          details: [],
        });
      }

      const merged = groups[tradeKey].itemsMap.get(mergeKey);
      merged.quantity += qty;
      merged.estimatedCost += estimatedCost;
      merged.details.push({
        id: pi.id,
        spaceName: spacesMap.get(pi.space_id)?.custom_space_name || null,
        options: pi.selected_option ? formatSelectedOption(pi.selected_option) || null : null,
        quantity: qty,
        estimatedCost,
      });

      groups[tradeKey].projectItemIds.push(pi.id);
      groups[tradeKey].subtotal += estimatedCost;
    });

    // Flatten itemsMap → items[] and sort by trade category/name
    return Object.values(groups)
      .map(g => ({
        tradeKey: g.tradeKey,
        tradeName: g.tradeName,
        tradeVendorTypeId: g.tradeVendorTypeId,
        category: g.category,
        subtotal: g.subtotal,
        projectItemIds: g.projectItemIds,
        items: Array.from(g.itemsMap.values()).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'he'),
        ),
      }))
      .sort((a, b) => {
        if (a.category !== b.category) return a.category.localeCompare(b.category, 'he');
        return a.tradeName.localeCompare(b.tradeName, 'he');
      });
  }, [projectItems, masterItemsMap, vendorTypesMap, spacesMap]);

  // Grand total of all estimated costs
  const grandTotal = useMemo(
    () => groupedItems.reduce((sum, g) => sum + g.subtotal, 0),
    [groupedItems],
  );

  const toggleRow = key => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTrade = tradeKey => {
    setExpandedTrades(prev => {
      const next = new Set(prev);
      if (next.has(tradeKey)) next.delete(tradeKey);
      else next.add(tradeKey);
      return next;
    });
  };

  const handleOpenTradeQuotes = group => {
    openModal(MODAL_TYPES.TRADE_QUOTES, {
      projectId,
      tradeName: group.tradeName,
      tradeVendorTypeId: group.tradeVendorTypeId,
      projectItemIds: group.projectItemIds,
    });
  };

  if (isLoading) {
    return (
      <div className={pageStyles.loadingContainer}>
        <div className={tableStyles.spinner} />
      </div>
    );
  }

  if (groupedItems.length === 0) {
    return (
      <div className={tableStyles.emptyState}>
        <p className={tableStyles.emptyTitle}>כל הפריטים מכוסים בהצעות מחיר</p>
      </div>
    );
  }

  return (
    <div>
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead>
            <tr className={tableStyles.headerRow}>
              <th className={tableStyles.headerCell}>פריט</th>
              <th className={tableStyles.headerCell}>כמות</th>
              <th className={tableStyles.headerCell}>עלות משוערת</th>
            </tr>
          </thead>
          <tbody>
            {groupedItems.map(group => {
              const isTradeExpanded = expandedTrades.has(group.tradeKey);
              return (
                <React.Fragment key={group.tradeKey}>
                  {/* Trade type header — clickable to expand/collapse */}
                  <tr
                    className={`${tableStyles.categoryHeader} ${styles.tradeHeaderRow}`}
                    onClick={() => toggleTrade(group.tradeKey)}
                  >
                    <td colSpan={2}>
                      <div className={styles.tradeNameGroup}>
                        <span className={styles.tradeCaret}>
                          {isTradeExpanded ? <CaretDown size={14} /> : <CaretLeft size={14} />}
                        </span>
                        <span>{group.tradeName}</span>
                        <span className={styles.itemCount}>({group.items.length} פריטים)</span>
                        <button
                          type="button"
                          className={styles.quotesButton}
                          onClick={e => {
                            e.stopPropagation();
                            handleOpenTradeQuotes(group);
                          }}
                        >
                          הצעות מחיר
                        </button>
                      </div>
                    </td>
                    <td>{formatCurrency(group.subtotal)}</td>
                  </tr>

                  {/* Merged rows within this trade — only when expanded */}
                  {isTradeExpanded &&
                    group.items.map(item => {
                      const expandable = item.details.length > 1;
                      const rowKey = `${group.tradeKey}::${item.key}`;
                      const isExpanded = expandedKeys.has(rowKey);
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className={`${tableStyles.row} ${expandable ? styles.clickableRow : ''}`}
                            onClick={expandable ? () => toggleRow(rowKey) : undefined}
                          >
                            <td className={`${tableStyles.cell} ${tableStyles.cellPrimary}`}>
                              {expandable && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    verticalAlign: 'middle',
                                    marginLeft: 6,
                                    color: '#64748b',
                                  }}
                                >
                                  {isExpanded ? <CaretDown size={14} /> : <CaretLeft size={14} />}
                                </span>
                              )}
                              {item.name}
                            </td>
                            <td className={tableStyles.cell}>{item.quantity}</td>
                            <td className={tableStyles.cell}>
                              {item.estimatedCost > 0 ? formatCurrency(item.estimatedCost) : '—'}
                            </td>
                          </tr>

                          {/* Expanded detail rows — one per underlying project_item */}
                          {expandable &&
                            isExpanded &&
                            item.details.map(detail => (
                              <tr key={detail.id} className={tableStyles.row}>
                                <td
                                  className={tableStyles.cell}
                                  style={{ paddingInlineStart: 32, background: '#f9fafb' }}
                                >
                                  <div className={tableStyles.cellSecondary}>
                                    {detail.spaceName || 'ללא חלל'}
                                  </div>
                                  {detail.options && (
                                    <div
                                      className={tableStyles.cellSecondary}
                                      style={{ marginTop: 2 }}
                                    >
                                      {detail.options}
                                    </div>
                                  )}
                                </td>
                                <td className={tableStyles.cell} style={{ background: '#f9fafb' }}>
                                  {detail.quantity}
                                </td>
                                <td className={tableStyles.cell} style={{ background: '#f9fafb' }}>
                                  {detail.estimatedCost > 0
                                    ? formatCurrency(detail.estimatedCost)
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}

            {/* Grand total */}
            <tr className={styles.totalRowWarning}>
              <td className={tableStyles.cell} colSpan={2}>
                סה״כ עלות משוערת (חסר הצעות)
              </td>
              <td className={tableStyles.cell}>{formatCurrency(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NeedsQuotesTab;
